import React, { useState, useEffect } from "react";
import { Alert, Col, DatePicker, Input, InputNumber, Row, Select, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { langDate } from "../../../../utils/properties";
import { filter, get, isEmpty, toLower } from "lodash";
import { useSelector } from "react-redux";
import ExpenseAccountType from "../../../../services/api/ExpenseAccountType";
import FuelCard from "../../../../services/api/FuelCard";
import dayjs from "dayjs";

const { Option } = Select;

function Justification({ form, detailFolder, modalViewData, canEdit }) {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const dateFormat = langDate[toLower(currentLanguage)];
  const [state, setState] = useState({
    expenseAccountTypeList: [],
  });

  useEffect(() => {
    expenseAccountType();
  }, []);

  const expenseAccountType = async () => {
    let expenseAccountTypeList = [];
    const { error: errorFuelCard, data: dataFuelCard } = await FuelCard.getFuelCards(
      `user.username=${userInfo.username}`
    );
    if (!errorFuelCard && dataFuelCard) {
      // if user have a fuel card, remove category "fuel" for list
      const { error, data } = await ExpenseAccountType.getExpenseAccountTypes(`allPages=true`);
      if (!error && data) {
        if (!isEmpty(dataFuelCard)) {
          expenseAccountTypeList = filter(data, (d) => d.category !== "FUEL");
        } else {
          expenseAccountTypeList = data;
        }
      }
    }
    if (detailFolder?.flatRate) {
      // if user check flat rate on folder creation, remove category "meal" for list
      expenseAccountTypeList = filter(expenseAccountTypeList, (c) => c.category !== "MEAL");
    }
    setState((prevState) => ({ ...prevState, expenseAccountTypeList }));
  };

  const amountInputClassnames = () => {
    let classnames = "";
    if (modalViewData) {
      classnames = "w-100";
    } else {
      classnames = "ant-input";
      if (form?.errors?.amount) {
        classnames += " input-error";
      }
    }
    return classnames;
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Alert
            message={
              <>
                {t("expenseRefundFolder.justificationInfoBox")}{" "}
                <a
                  href="https://tassidicambio.bancaditalia.it/terzevalute-wf-ui-web/dailyRates"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://tassidicambio.bancaditalia.it/terzevalute-wf-ui-web/dailyRates
                </a>
              </>
            }
            type="info"
          />
        </Col>
      </Row>
      <Row gutter={[40, 24]} className="mb-10" justify="space-around">
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="amount">
              {t("expenseRefundFolder.table.amount")}*
            </label>
          </Row>
          <Row>
            <InputNumber
              className={amountInputClassnames()}
              controls={false}
              required
              id="amount"
              name="amount"
              onChange={(e) => form?.setFieldValue("amount", e)}
              onBlur={form?.handleBlur}
              value={modalViewData ? modalViewData.amount : form?.values.amount}
              precision={2}
              decimalSeparator=","
              min={0}
              disabled={modalViewData || !canEdit}
            />
          </Row>
          {form?.errors?.amount ? (
            <Row>
              <span className="invalid-field-error">{t("expenseRefundFolder.errorZero")}</span>
            </Row>
          ) : (
            ""
          )}
        </Col>
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="invoicePfp">
              {t("expenseRefundFolder.table.invoicePfp")}
            </label>
          </Row>
          <Row>
            <Switch
              id="invoicePfp"
              name="invoicePfp"
              onChange={(e) => {
                if (e) {
                  const useInvoice = filter(state.expenseAccountTypeList, (a) => a.category === "INVOICE");
                  if (!isEmpty(useInvoice)) {
                    form?.setFieldValue("expenseAccountType", useInvoice[0].id);
                  }
                } else {
                  form?.setFieldValue("expenseAccountType", null);
                }
                form?.setFieldValue("supplierName", null);
                form?.setFieldValue("supplierVat", null);
                form?.setFieldValue("invoicePfp", e);
              }}
              onBlur={form?.handleBlur("invoicePfp")}
              checked={modalViewData ? modalViewData.invoicePfp : form?.values.invoicePfp}
              checkedChildren={t("general.yes")}
              unCheckedChildren={t("general.no")}
              disabled={modalViewData || !canEdit}
            />
          </Row>
        </Col>
      </Row>
      <Row
        gutter={[40, 24]}
        className="mb-10"
        justify="space-around"
        style={{ display: form?.values.invoicePfp ? "flex" : "none" }}
      >
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="supplierName">
              {t("expenseRefundFolder.table.supplierName")}
            </label>
          </Row>
          <Row>
            <Input
              required
              id="supplierName"
              name="supplierName"
              onChange={form?.handleChange}
              onBlur={form?.handleBlur}
              value={modalViewData ? modalViewData.supplierName : form?.values.supplierName}
              disabled={modalViewData || !canEdit}
            />
          </Row>
        </Col>
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="supplierVat">
              {t("expenseRefundFolder.table.supplierVat")}
            </label>
          </Row>
          <Row>
            <Input
              required
              id="supplierVat"
              name="supplierVat"
              onChange={form?.handleChange}
              onBlur={form?.handleBlur}
              value={modalViewData ? modalViewData.supplierVat : form?.values.supplierVat}
              disabled={modalViewData || !canEdit}
            />
          </Row>
        </Col>
      </Row>
      <Row gutter={[40, 24]} className="mb-10" justify="space-around">
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="docDate">
              {t("expenseRefundFolder.table.docDate")}*
            </label>
          </Row>
          <DatePicker
            className="w-100"
            allowClear={false}
            id="docDate"
            name="docDate"
            format={dateFormat}
            locale={langDate[`locale_${toLower(currentLanguage)}`]}
            onChange={(date) => {
              form?.setFieldValue("docDate", date);
            }}
            onBlur={form?.handleBlur("docDate")}
            value={modalViewData ? dayjs(modalViewData.docDate) : form?.values.docDate}
            disabledDate={(current) => {
              // Set the minimum and maximum allowed dates
              const minDate = dayjs(detailFolder?.periodStart);
              const maxDate = dayjs(detailFolder?.periodEnd).add(1, "days");
              // Disable all dates before minDate and after maxDate, except maxDate itself
              return current && (current < minDate || current > maxDate);
            }}
            disabled={modalViewData || !canEdit}
          />
        </Col>
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="docNumber">
              {form?.values.invoicePfp || modalViewData?.invoicePfp
                ? t("expenseRefundFolder.table.docNumberInvoice")
                : t("expenseRefundFolder.table.docNumber")}
              *
            </label>
          </Row>
          <Row>
            <Input
              required
              id="docNumber"
              name="docNumber"
              onChange={form?.handleChange}
              onBlur={form?.handleBlur}
              value={modalViewData ? modalViewData.docNumber : form?.values.docNumber}
              disabled={modalViewData || !canEdit}
              maxLength={20}
              showCount
            />
          </Row>
        </Col>
      </Row>
      <Row gutter={[40, 24]} className="mb-10" justify="space-around">
        <Col lg={22} md={22} xs={24} sm={22}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="expenseAccountType">
              {t("expenseRefundFolder.table.expenseAccountType")}*
            </label>
          </Row>
          <Select
            allowClear
            className="w-100"
            id="expenseAccountType"
            optionFilterProp="value"
            name="expenseAccountType"
            placeholder={t("expenseRefundFolder.table.expenseAccountType")}
            onChange={(e) => form?.setFieldValue("expenseAccountType", e)}
            onBlur={form?.handleBlur("expenseAccountType")}
            value={modalViewData ? modalViewData.expenseAccountType?.id : form?.values.expenseAccountType}
            disabled={modalViewData || form?.values.invoicePfp || !canEdit}
          >
            {state.expenseAccountTypeList.map((expenseAccountType) => (
              <Option key={expenseAccountType.id} value={expenseAccountType.id} name={expenseAccountType.description}>
                {" "}
                {expenseAccountType.description}{" "}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={[40, 24]} className="mb-10" justify="space-around">
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="note">
              {t("expenseRefundFolder.table.note")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="note"
              name="note"
              onChange={form?.handleChange}
              onBlur={form?.handleBlur}
              value={modalViewData ? modalViewData.note : form?.values.note}
              disabled={modalViewData || !canEdit}
            />
          </Row>
        </Col>
        <Col lg={10} md={10} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="traceablePayment">
              {t("expenseRefundFolder.table.traceablePayment")}
            </label>
          </Row>
          <Row>
            <Switch
              id="traceablePayment"
              name="traceablePayment"
              onChange={(e) => form?.setFieldValue("traceablePayment", e)}
              onBlur={form?.handleBlur("traceablePayment")}
              checked={modalViewData ? modalViewData.traceablePayment : form?.values.traceablePayment}
              checkedChildren={t("general.yes")}
              unCheckedChildren={t("general.no")}
              disabled={modalViewData || !canEdit}
            />
          </Row>
        </Col>
      </Row>
    </>
  );
}

export default Justification;
